import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, FileText, ArrowLeft, FileUp, PenLine, CheckCircle,
  ChevronDown, FlaskConical, Save, Loader2, Plus, X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEST_CATEGORIES = [
  { name: 'Hemograma Completo', color: '#EF4444', tests: [
    { key: 'hemoglobina', label: 'Hemoglobina', unit: 'g/dL' },
    { key: 'hematocrito', label: 'Hematocrito', unit: '%' },
    { key: 'globulos_rojos', label: 'Globulos Rojos', unit: 'x10⁶/µL' },
    { key: 'vcm', label: 'VCM', unit: 'fL' },
    { key: 'hcm', label: 'HCM', unit: 'pg' },
    { key: 'chcm', label: 'CHCM', unit: 'g/dL' },
    { key: 'rdw_cv', label: 'RDW-CV', unit: '%' },
  ]},
  { name: 'Globulos Blancos', color: '#EC4899', tests: [
    { key: 'globulos_blancos', label: 'Globulos Blancos', unit: 'x10³/µL' },
    { key: 'neutrofilos', label: 'Neutrofilos', unit: 'x10³/µL' },
    { key: 'linfocitos', label: 'Linfocitos', unit: 'x10³/µL' },
    { key: 'monocitos', label: 'Monocitos', unit: 'x10³/µL' },
    { key: 'eosinofilos', label: 'Eosinofilos', unit: 'x10³/µL' },
    { key: 'basofilos', label: 'Basofilos', unit: 'x10³/µL' },
  ]},
  { name: 'Recuento Diferencial', color: '#F97316', tests: [
    { key: 'neutrofilos_pct', label: 'Neutrofilos %', unit: '%' },
    { key: 'linfocitos_pct', label: 'Linfocitos %', unit: '%' },
    { key: 'monocitos_pct', label: 'Monocitos %', unit: '%' },
    { key: 'eosinofilos_pct', label: 'Eosinofilos %', unit: '%' },
    { key: 'basofilos_pct', label: 'Basofilos %', unit: '%' },
  ]},
  { name: 'Proteinas Especiales', color: '#A855F7', tests: [
    { key: 'fibrinogeno', label: 'Fibrinogeno', unit: 'mg/dL' },
    { key: 'ceruloplasmina', label: 'Ceruloplasmina', unit: 'mg/dL' },
    { key: 'iga', label: 'IGA', unit: 'mg/dL' },
    { key: 'igg', label: 'IGG', unit: 'mg/dL' },
  ]},
  { name: 'Quimica Sanguinea', color: '#3B82F6', tests: [
    { key: 'acido_folico', label: 'Acido Folico', unit: 'ng/mL' },
    { key: 'acido_urico', label: 'Acido Urico', unit: 'mg/dL' },
    { key: 'alt', label: 'ALT (SGPT)', unit: 'U/L' },
    { key: 'amilasa', label: 'Amilasa', unit: 'U/L' },
    { key: 'ast', label: 'AST (SGOT)', unit: 'U/L' },
    { key: 'bilirrubina', label: 'Bilirrubina', unit: 'mg/dL' },
    { key: 'bun', label: 'BUN', unit: 'mg/dL' },
    { key: 'calcio', label: 'Calcio', unit: 'mg/dL' },
    { key: 'c_hdl', label: 'C-HDL', unit: 'mg/dL' },
    { key: 'c_ldl', label: 'C-LDL', unit: 'mg/dL' },
    { key: 'c_vldl', label: 'C-VLDL', unit: 'mg/dL' },
    { key: 'colesterol_total', label: 'Colesterol Total', unit: 'mg/dL' },
    { key: 'co2', label: 'Co2', unit: 'mEq/L' },
    { key: 'colinesterasa', label: 'Colinesterasa Plasmatica', unit: 'U/L' },
    { key: 'creatinina', label: 'Creatinina', unit: 'mg/dL' },
    { key: 'ferritina', label: 'Ferritina', unit: 'ng/mL' },
    { key: 'fosfatasa_alc', label: 'Fosfatasa Alcalina', unit: 'U/L' },
    { key: 'ggt', label: 'GGT', unit: 'U/L' },
    { key: 'glucosa', label: 'Glucosa', unit: 'mg/dL' },
    { key: 'hba1c', label: 'Hemoglobina Glicosilada AIC', unit: '%' },
    { key: 'hierro', label: 'Hierro y Captacion', unit: 'µg/dL' },
    { key: 'homocisteina', label: 'Homocisteina', unit: 'µmol/L' },
    { key: 'ige', label: 'IGE', unit: 'IU/mL' },
    { key: 'ldh', label: 'LDH', unit: 'U/L' },
    { key: 'lipasa', label: 'Lipasa', unit: 'U/L' },
    { key: 'magnesio', label: 'Magnesio', unit: 'mg/dL' },
    { key: 'potasio', label: 'Potasio', unit: 'mEq/L' },
    { key: 'transferrina', label: 'Transferrina', unit: 'mg/dL' },
    { key: 'trigliceridos', label: 'Trigliceridos', unit: 'mg/dL' },
    { key: 'vitamina_b12', label: 'Vitamina B12', unit: 'pg/mL' },
    { key: 'vitamina_d', label: 'Vitamina D (25 OG) Total', unit: 'ng/mL' },
  ]},
  { name: 'Hormonas', color: '#8B5CF6', tests: [
    { key: 'acth', label: 'ACTH', unit: 'pg/mL' },
    { key: 'anti_tiroglobulina', label: 'Anti Tiroglobulina', unit: 'IU/mL' },
    { key: 'anti_tpo', label: 'Anti TPO', unit: 'IU/mL' },
    { key: 'cortisol_am', label: 'Cortisol AM', unit: 'µg/dL' },
    { key: 'dhea_s04', label: 'DHEA-S04', unit: 'µg/dL' },
    { key: 'estradiol', label: 'Estradiol 17B Estradiol', unit: 'pg/mL' },
    { key: 'fsh', label: 'FSH', unit: 'mIU/mL' },
    { key: 'igf_1', label: 'IGF-1', unit: 'ng/mL' },
    { key: 'insulina', label: 'Insulina', unit: 'µIU/mL' },
    { key: 'lh', label: 'LH', unit: 'mIU/mL' },
    { key: 'peptido_c', label: 'Peptido C', unit: 'ng/mL' },
    { key: 'progesterona', label: 'Progesterona', unit: 'ng/mL' },
    { key: 'prolactina', label: 'Prolactina', unit: 'ng/mL' },
    { key: 'pth', label: 'PTH Intacta', unit: 'pg/mL' },
    { key: 'shbg', label: 'SHBG', unit: 'nmol/L' },
    { key: 't3', label: 'T3', unit: 'ng/dL' },
    { key: 't3_libre', label: 'T3 Libre', unit: 'pg/mL' },
    { key: 't4', label: 'T4', unit: 'µg/dL' },
    { key: 't4_libre', label: 'T4 Libre', unit: 'ng/dL' },
    { key: 'testosterona', label: 'Testosterona', unit: 'ng/dL' },
    { key: 'tiroglobulina', label: 'Tiroglobulina', unit: 'ng/mL' },
    { key: 'tsh', label: 'TSH', unit: 'µIU/mL' },
  ]},
  { name: 'Inflamacion', color: '#DC2626', tests: [
    { key: 'pcr_alta', label: 'PCR Alta Sensibilidad', unit: 'mg/L' },
  ]},
];

const CategorySection = ({ category, results, onUpdate, defaultOpen = false, extractedOnly = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const visibleTests = extractedOnly
    ? category.tests.filter(t => results[t.key]?.value)
    : category.tests;
  if (extractedOnly && visibleTests.length === 0) return null;
  const filledCount = category.tests.filter(t => results[t.key]?.value).length;
  return (
    <div className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors" data-testid={`cat-${category.name}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${category.color}12` }}>
            <FlaskConical size={18} style={{ color: category.color }} />
          </div>
          <div className="text-left">
            <p className="font-bold text-gray-900 text-sm">{category.name}</p>
            <span className="text-[10px] text-gray-400">
              {extractedOnly
                ? `${visibleTests.length} ${visibleTests.length === 1 ? 'resultado extraido' : 'resultados extraidos'}`
                : `${filledCount}/${category.tests.length} registrados`}
            </span>
          </div>
        </div>
        <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4 space-y-3 animate-fade-in">
          {visibleTests.map((test) => (
            <div key={test.key} className="flex items-center gap-3">
              <label className="flex-1 text-sm text-gray-600 min-w-0 font-medium">{test.label}</label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Input
                  value={results[test.key]?.value || ''}
                  onChange={(e) => onUpdate(test.key, e.target.value, test.unit)}
                  placeholder="—"
                  className="w-24 h-9 text-center text-sm bg-gray-50 border-transparent focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 rounded-lg"
                  data-testid={`input-${test.key}`}
                />
                {test.unit && <span className="text-[11px] text-gray-400 w-16 text-left">{test.unit}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ClientUploadAnalyticsPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testDate, setTestDate] = useState('');
  const [testName, setTestName] = useState('');
  const [results, setResults] = useState({});
  const [pdfResult, setPdfResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  // Custom (user-added) tests not present in the built-in catalog
  const [customTests, setCustomTests] = useState([]); // [{key, label, unit}]
  const [addOpen, setAddOpen] = useState(false);
  const [addingTest, setAddingTest] = useState(false);
  const [newTest, setNewTest] = useState({ label: '', value: '', unit: '' });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo se aceptan archivos PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('El archivo no debe superar 10MB'); return; }
    setSelectedFile(file);
  };

  const handleUploadPdf = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (testDate) formData.append('test_date', testDate);
      const response = await axios.post(`${API}/blood-analytics/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      setPdfResult(response.data);
      const extracted = response.data.analytics.results || {};
      setResults(extracted);
      // Capture any custom-labelled keys returned by the AI (already-registered custom tests)
      const detectedCustom = Object.entries(extracted)
        .filter(([, v]) => v?.custom && v?.label)
        .map(([k, v]) => ({ key: k, label: v.label, unit: v.unit || '' }));
      setCustomTests(detectedCustom);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el PDF');
    } finally { setUploading(false); }
  };

  const handleUpdateResult = (key, value, unit) => {
    setResults(prev => ({ ...prev, [key]: { ...(prev[key] || {}), value, unit: unit ?? prev[key]?.unit } }));
  };

  const handleAddCustomResult = async () => {
    const label = newTest.label.trim();
    const value = newTest.value.trim();
    const unit = newTest.unit.trim();
    if (!label) { toast.error('Ingresa el nombre del estudio'); return; }
    if (!value) { toast.error('Ingresa el valor del resultado'); return; }
    setAddingTest(true);
    try {
      const { data } = await axios.post(`${API}/blood-analytics/custom-tests`, { label, unit });
      const key = data.key;
      const resolvedLabel = data.label || label;
      const resolvedUnit = data.unit || unit;
      setResults(prev => ({
        ...prev,
        [key]: { value, unit: resolvedUnit, label: resolvedLabel, custom: !data.builtin },
      }));
      if (!data.builtin) {
        setCustomTests(prev => {
          if (prev.some(c => c.key === key)) {
            return prev.map(c => c.key === key ? { ...c, label: resolvedLabel, unit: resolvedUnit } : c);
          }
          return [...prev, { key, label: resolvedLabel, unit: resolvedUnit }];
        });
      }
      setNewTest({ label: '', value: '', unit: '' });
      setAddOpen(false);
      toast.success(data.builtin ? 'Resultado agregado' : 'Estudio agregado al catalogo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo agregar el estudio');
    } finally { setAddingTest(false); }
  };

  const handleRemoveCustomFromList = (key) => {
    setResults(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCustomTests(prev => prev.filter(c => c.key !== key));
  };

  const handleSaveManual = async () => {
    const filled = Object.values(results).filter(v => v.value).length;
    if (filled === 0) { toast.error('Ingresa al menos un resultado'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/blood-analytics/manual-entry`, { test_date: testDate, test_name: testName || 'Analitica Manual', results });
      toast.success('Resultados guardados exitosamente');
      navigate('/client/analytics');
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleSaveEdited = async () => {
    if (!pdfResult?.analytics?.id) return;
    setSaving(true);
    try {
      const filled = Object.values(results).filter(v => v.value).length;
      await axios.put(`${API}/blood-analytics/${pdfResult.analytics.id}`, { results, status: filled > 0 ? 'completed' : 'pending' });
      toast.success('Resultados actualizados');
      navigate('/client/analytics');
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar'); }
    finally { setSaving(false); }
  };

  // Mode selection
  if (!mode) {
    return (
      <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="upload-analytics-page">
        <button onClick={() => navigate('/client/analytics')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-btn">
          <ArrowLeft size={16} /> Analiticas de Sangre
        </button>
        <div className="pt-1">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Subir Analitica</h1>
          <p className="text-sm text-gray-400 mt-1 font-medium">Elige como deseas cargar tus resultados</p>
        </div>
        <div className="space-y-3">
          <button onClick={() => setMode('pdf')} className="w-full text-left bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid="mode-pdf">
            <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-600" />
            <div className="p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
                <FileUp size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Cargar Archivo PDF</p>
                <p className="text-xs text-gray-400 mt-0.5">La IA leera tu documento y extraera los resultados automaticamente</p>
              </div>
            </div>
          </button>
          <button onClick={() => setMode('manual')} className="w-full text-left bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid="mode-manual">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600" />
            <div className="p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                <PenLine size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Entrada Manual</p>
                <p className="text-xs text-gray-400 mt-0.5">Ingresa los resultados uno a uno en el formulario</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // PDF upload mode
  if (mode === 'pdf' && !pdfResult) {
    return (
      <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="pdf-upload-view">
        <button onClick={() => setMode(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <ArrowLeft size={16} /> Cambiar metodo
        </button>
        <div className="pt-1">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Cargar PDF</h1>
          <p className="text-sm text-gray-400 mt-1">La IA interpretara tu documento de laboratorio</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Fecha del estudio (opcional)</label>
            <Input type="text" placeholder="DD/MM/AAAA" value={testDate} onChange={e => setTestDate(e.target.value)}
              className="bg-white border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 rounded-xl h-11" data-testid="pdf-date-input" />
          </div>
          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center hover:border-violet-400 transition-colors cursor-pointer" onClick={() => document.getElementById('pdf-input').click()}>
            <input id="pdf-input" type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" data-testid="pdf-file-input" />
            {selectedFile ? (
              <div className="space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto"><FileText size={28} className="text-violet-500" /></div>
                <p className="font-bold text-gray-900 text-sm">{selectedFile.name}</p>
                <p className="text-xs text-gray-400">{(selectedFile.size / 1024).toFixed(0)} KB</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto"><Upload size={28} className="text-gray-300" /></div>
                <p className="font-bold text-gray-600 text-sm">Seleccionar archivo PDF</p>
                <p className="text-xs text-gray-400">Maximo 10MB</p>
              </div>
            )}
          </div>
          <Button onClick={handleUploadPdf} disabled={!selectedFile || uploading}
            className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white py-5 text-base font-bold rounded-full shadow-lg shadow-violet-500/30 disabled:opacity-50" data-testid="upload-pdf-btn">
            {uploading ? (<div className="flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Procesando con IA...</div>)
              : (<div className="flex items-center gap-2"><Upload size={18} /> Subir y Analizar</div>)}
          </Button>
        </div>
      </div>
    );
  }

  // Results form (post-PDF or manual)
  const isPostPdf = !!pdfResult;
  const filledCount = Object.values(results).filter(v => v.value).length;
  const totalTests = TEST_CATEGORIES.reduce((sum, c) => sum + c.tests.length, 0);
  const visibleCategories = isPostPdf
    ? TEST_CATEGORIES.filter(cat => cat.tests.some(t => results[t.key]?.value))
    : TEST_CATEGORIES;

  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fade-in pb-8" data-testid={isPostPdf ? 'pdf-results-view' : 'manual-entry-view'}>
      <button onClick={() => isPostPdf ? navigate('/client/analytics') : setMode(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeft size={16} /> {isPostPdf ? 'Analiticas' : 'Cambiar metodo'}
      </button>
      <div className="pt-1">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {isPostPdf ? 'Resultados Extraidos' : 'Entrada Manual'}
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          {isPostPdf ? `${pdfResult.extracted_count} resultados encontrados - puedes editar antes de guardar` : 'Ingresa los valores de tu estudio'}
        </p>
      </div>
      {!isPostPdf && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Fecha del estudio</label>
            <Input type="text" placeholder="DD/MM/AAAA" value={testDate} onChange={e => setTestDate(e.target.value)}
              className="bg-white border-gray-200 focus:border-blue-500 rounded-xl h-11" data-testid="manual-date-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Nombre del estudio</label>
            <Input type="text" placeholder="Ej: Chequeo General" value={testName} onChange={e => setTestName(e.target.value)}
              className="bg-white border-gray-200 focus:border-blue-500 rounded-xl h-11" data-testid="manual-name-input" />
          </div>
        </div>
      )}
      {isPostPdf && pdfResult.extracted_count > 0 && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-4 text-white shadow-lg shadow-emerald-500/20 flex items-center gap-3">
          <CheckCircle size={22} />
          <div>
            <p className="font-bold text-sm">IA extrajo {pdfResult.extracted_count} resultados</p>
            <p className="text-xs text-white/70">Solo se muestran las analiticas detectadas en tu PDF</p>
          </div>
        </div>
      )}
      {isPostPdf && pdfResult.extracted_count === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900">
          <p className="font-bold text-sm">La IA no detecto resultados</p>
          <p className="text-xs mt-1">Intenta con otro PDF o usa la entrada manual.</p>
        </div>
      )}
      {!isPostPdf && (
        <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-4 text-white shadow-lg shadow-violet-500/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-white/80">Progreso</span>
            <span className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{Math.round((filledCount / totalTests) * 100)}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${(filledCount / totalTests) * 100}%` }} />
          </div>
          <p className="text-xs text-white/60 mt-1.5">{filledCount} de {totalTests} pruebas</p>
        </div>
      )}
      <div className="space-y-3">
        {visibleCategories.map(cat => (
          <CategorySection
            key={cat.name}
            category={cat}
            results={results}
            onUpdate={handleUpdateResult}
            defaultOpen={isPostPdf}
            extractedOnly={isPostPdf}
          />
        ))}

        {customTests.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm">
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-50">
                <Plus size={18} className="text-amber-500" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-sm">Agregados manualmente</p>
                <span className="text-[10px] text-gray-400">{customTests.length} {customTests.length === 1 ? 'resultado' : 'resultados'}</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {customTests.map((c) => (
                <div key={c.key} className="flex items-center gap-3" data-testid={`custom-row-${c.key}`}>
                  <label className="flex-1 text-sm text-gray-600 min-w-0 font-medium truncate">{c.label}</label>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Input
                      value={results[c.key]?.value || ''}
                      onChange={(e) => handleUpdateResult(c.key, e.target.value, c.unit)}
                      placeholder="—"
                      className="w-24 h-9 text-center text-sm bg-gray-50 border-transparent focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 rounded-lg"
                      data-testid={`custom-input-${c.key}`}
                    />
                    {c.unit && <span className="text-[11px] text-gray-400 w-16 text-left">{c.unit}</span>}
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomFromList(c.key)}
                      className="w-7 h-7 rounded-full bg-gray-50 hover:bg-rose-50 text-gray-400 hover:text-rose-500 flex items-center justify-center transition-colors"
                      aria-label="Quitar"
                      data-testid={`custom-remove-${c.key}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="w-full bg-white border-2 border-dashed border-violet-200 hover:border-violet-400 hover:bg-violet-50/40 transition-colors rounded-2xl p-4 flex items-center justify-center gap-2 text-violet-600 font-bold text-sm"
          data-testid="add-custom-result-btn"
        >
          <Plus size={18} /> Agregar resultado faltante
        </button>
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setNewTest({ label: '', value: '', unit: '' }); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="add-custom-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-xl">
              Agregar resultado faltante
            </DialogTitle>
            <p className="text-xs text-gray-400 mt-1">
              Si la IA no detecto algun estudio, agregalo aqui. Los nuevos estudios se guardan en el catalogo para que la IA los reconozca en futuras analiticas.
            </p>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Nombre del estudio</label>
              <Input
                type="text"
                placeholder="Ej: Vitamina C"
                value={newTest.label}
                onChange={(e) => setNewTest(prev => ({ ...prev, label: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white focus:border-violet-500 rounded-xl h-11"
                data-testid="custom-label-input"
                maxLength={80}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Valor</label>
                <Input
                  type="text"
                  placeholder="Ej: 1.2"
                  value={newTest.value}
                  onChange={(e) => setNewTest(prev => ({ ...prev, value: e.target.value }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white focus:border-violet-500 rounded-xl h-11 text-center"
                  data-testid="custom-value-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Unidad</label>
                <Input
                  type="text"
                  placeholder="Ej: mg/dL"
                  value={newTest.unit}
                  onChange={(e) => setNewTest(prev => ({ ...prev, unit: e.target.value }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white focus:border-violet-500 rounded-xl h-11 text-center"
                  data-testid="custom-unit-input"
                  maxLength={20}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="pt-2 gap-2">
            <Button
              variant="ghost"
              onClick={() => { setAddOpen(false); setNewTest({ label: '', value: '', unit: '' }); }}
              className="rounded-full"
              data-testid="custom-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddCustomResult}
              disabled={addingTest || !newTest.label.trim() || !newTest.value.trim()}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-full font-bold disabled:opacity-50"
              data-testid="custom-confirm-btn"
            >
              {addingTest ? (<div className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Agregando...</div>) : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pt-2 pb-4">
        <Button onClick={isPostPdf ? handleSaveEdited : handleSaveManual} disabled={saving || (isPostPdf && filledCount === 0)}
          className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white py-5 text-base font-bold rounded-full shadow-lg shadow-violet-500/30 disabled:opacity-50" data-testid="save-results-btn">
          {saving ? (<div className="flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Guardando...</div>)
            : (<div className="flex items-center gap-2"><Save size={18} /> Guardar Resultados</div>)}
        </Button>
      </div>
    </div>
  );
};

export default ClientUploadAnalyticsPage;
